import type { Sender, UserMessage } from '@sendbird/chat/message';
import type { Dispatch, SetStateAction } from 'react';

// We export these types here in order to avoid importing from the Sendbird SDK directly in the components.
export type { Sender };
export type ChatMessage = UserMessage;

export type ChatUser = {
  userId: string;
  nickname: string;
};

export enum MessageCustomType {
  ANONYMOUS = 'ANONYMOUS',
  FROM_MODERATOR = 'FROM_MODERATOR',
  APPROVED_BY_MODERATOR = 'APPROVED_BY_MODERATOR',
  DECLINED_BY_MODERATOR = 'DECLINED_BY_MODERATOR',
  ANSWERED = 'ANSWERED',
}

export type Action = {
  id: string;
  type: ReactionKey | 'vote';
  messageId: number;
  userId: string;
};

export type ChatConfig = {
  applicationId?: string;
  channelUrl?: string;
  eventId: string;
  chatUserId: string;
  nickname?: string;
  apiHost: string;
  wsHost: string;
};

export type Chat = {
  user?: ChatUser;
  messages: ChatMessage[];
  userActionError?: string;
  providerError?: string;
  connected: boolean;
  connectedAt: number;
  connecting: boolean;
  sendingMessage: boolean;
  editingMessage: boolean;
  deletingMessage: boolean;
  actions: Action[];
  unreadCount: number;
  hasNewMessages: boolean;
  readRepliesIds: ChatMessage['messageId'][];
  latestUnreadModeratorReply?: ChatMessage;
  isReplyUnread: (messageId: ChatMessage['messageId']) => boolean;
  setModeratorReplies: Dispatch<SetStateAction<ChatMessage[]>>;
  setReadRepliesIds: Dispatch<SetStateAction<ChatMessage['messageId'][]>>;
  setHasNewMessages: Dispatch<SetStateAction<boolean>>;
  connectUser: (config: ChatConfig) => void;
  connectModerator: (config: ChatConfig) => void;
  sendMessage: (message: ChatMessage['message'], customTypes?: MessageCustomType[]) => Promise<boolean>;
  sendThreadMessage: (
    parentMessageId: ChatMessage['messageId'],
    message: ChatMessage['message'],
    customTypes?: MessageCustomType[]
  ) => Promise<ChatMessage | undefined>;
  editMessage: (
    messageId: ChatMessage['messageId'],
    message?: ChatMessage['message'],
    customTypes?: MessageCustomType[]
  ) => Promise<boolean>;
  deleteMessage: (message: ChatMessage) => Promise<boolean>;
  approveMessage: (message: ChatMessage) => Promise<boolean>;
  declineMessage: (message: ChatMessage) => Promise<boolean>;
  answerMessage: (message: ChatMessage) => Promise<boolean>;
  unanswerMessage: (message: ChatMessage) => Promise<boolean>;
  reactToMessage: (message: ChatMessage['messageId'], reactionName: ReactionKey) => void;
  getReactionsByMessageId: (messageId: ChatMessage['messageId']) => Reaction;
  getNumberOfReactionsByMessageId: (messageId: ChatMessage['messageId'], reactionName: ReactionKey) => number;
  hasUserReactedToMessage: (messageId: ChatMessage['messageId'], reactionName: ReactionKey) => boolean;
  voteForMessage: (message: ChatMessage) => void;
  getVotesByMessageId: (messageId: ChatMessage['messageId']) => number;
  hasUserVotedForMessage: (messageId: ChatMessage['messageId']) => boolean;
  disconnectChat: () => void;
};

export type ReactionKey = 'heart' | 'thumbsUp' | 'laugh';

export type Reaction = Record<ReactionKey, ChatUser['userId'][]>;

export type ChannelReactions = Record<string, Reaction | undefined>;

export type Votes = Record<string, ChatUser['userId'][] | undefined>;

export type ChannelMetadata = {
  reactions: ChannelReactions;
  votes: Votes;
};

export const reactionIcons = {
  heart: '❤️',
  thumbsUp: '👍',
  laugh: '😂',
};
